// @flow

import { all, call, put, takeEvery, select } from "redux-saga/effects";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import { stopSubmit, startSubmit, destroy } from "redux-form";
import { toast } from "react-toastify";
import http from "@http";
import config from "@config";
import sharedActionCreator from "@modules/shared/actionCreator";
import actionCreator from "@modules/reporting/actionCreator";
import * as actions from "@modules/reporting/constants/actions";
import { transformFilters, OPERATORS } from "@transformers/request";
import type { SelectedTask, OutstandingTask } from "@modules/reporting/reducer/tasks/types";
import { push } from "connected-react-router";
import type { ReferralDecisionTask } from "@modules/shared/reducer/absence-review/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";
import { getCurrentReport } from "@modules/reporting/selectors/reports/rtw-details";

type TasksRequest = $Exact<{
  clientId: number,
  taskId?: ?number,
  employeeId?: ?number,
  taskType?: ?string,
  page?: number,
  search?: $Exact<{
    lastName?: string,
  }>,
}>;

type OutstandingTaskRequest = $Exact<{
  clientId: number,
  taskId?: ?number,
  employeeId?: number,
  taskType?: ?string,
  page?: number,
  search: Object,
}>;

type TaskRequest = $Exact<{
  clientId: number,
  taskId: number,
  redirectToTaskPage?: boolean,
}>;

export function* onGetOutstandingTasks({
  payload,
}: Action<OutstandingTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const page = payload && payload.page;

    const searchFilter = transformFilters(
      {
        ...payload?.search,
        task_type: payload?.search?.taskType || [
          "RTW_INTERVIEW",
          "MANAGER_REFERRAL_DECISION",
          "ABSENCE_MONITORING_REVIEW",
        ],
      },

      { employee: OPERATORS.LIKE, task_type: OPERATORS.EQUALS },
    );

    const response = yield call(http.get, `api/v1/tools/client/${payload.clientId}/tools/task`, {
      params: {
        page,
        limit: config.middleResultsPerPage,
        sort: "due_date,asc",
        search: searchFilter,
      },
    });

    yield all([put(actionCreator.GetOutstandingTasksSuccess(response.data))]);

    if (payload.taskId && payload.taskType) {
      if (payload.taskType === "RTW_INTERVIEW") {
        yield put(
          actionCreator.GetRtwInterviewTask({
            clientId: payload.clientId,
            taskId: payload.taskId,
          }),
        );
      }
      if (payload.taskType === "ABSENCE_MONITORING_REVIEW") {
        yield put(
          actionCreator.GetAbsenceTask({
            clientId: payload.clientId,
            taskId: payload.taskId,
          }),
        );
      }
    }
  } catch (e) {
    yield put(actionCreator.GetOutstandingTasksError(e.response));
  }
}

export function* onGetAbsenceTaskSchema({
  payload,
}: Action<TasksRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("AbsenceMonitoringReviewTaskDTO", {
      clientId: payload.clientId,
    });

    yield all([put(actionCreator.GetAbsenceTaskSchemaSuccess(schema))]);
  } catch (e) {
    yield put(actionCreator.GetAbsenceTaskSchemaError(e.response));
  }
}

export function* onGetAbsenceTask({ payload }: Action<TaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}`,
    );

    yield all([put(actionCreator.GetAbsenceTaskSuccess(response.data))]);

    if (payload.redirectToTaskPage) {
      yield put(
        push(
          route("REPORTING_TASKS_FULL_PATH", {
            clientId: payload.clientId,
            taskId: payload.taskId,
            taskType: "monitoring",
          }),
        ),
      );
    }
  } catch (e) {
    yield put(actionCreator.GetAbsenceTaskError(e.response));
  }
}

export function* onGetManagerLedReferralTask({
  payload,
}: Action<TaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/manager-referral/${payload.taskId}`,
    );

    yield put(actionCreator.GetManagerLedReferralTaskSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetAbsenceTaskError(e.response));
  }
}

export function* onUpdateManagerLedReferralDraft({
  payload,
}: Action<{ clientId: number, task: ReferralDecisionTask }>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/manager-referral/${payload.task.id}`,
      payload.task,
    );

    yield all([
      put(actionCreator.UpdateManagerLedReferralDraftSuccess()),
      put(actionCreator.GetManagerLedReferralTaskSuccess(null)),
    ]);
  } catch (e) {
    yield put(actionCreator.UpdateManagerLedReferralDraftError(e.response));
  }
}
export function* onSubmitManagerLedReferralDraft({
  payload,
}: Action<{ clientId: number, task: ReferralDecisionTask }>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/manager-referral/${payload.task.id}/submit`,
      payload.task,
    );

    yield all([
      put(actionCreator.SubmitManagerLedReferralDraftSuccess()),
      put(actionCreator.GetOutstandingTasks({ clientId: payload.clientId })),
      put(actionCreator.GetManagerLedReferralTaskSuccess(null)),
      put(
        push(
          route("REPORTING_TOOLS_TASKS_PATH", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreator.SubmitManagerLedReferralDraftError(e.response));
  }
}

type SubmitTaskRequest = $Exact<{
  clientId: number,
  employeeId?: number,
  taskId: number,
  task: SelectedTask,
  isReports?: boolean,
}>;

export function* onSubmitAbsenceTask({
  payload,
}: Action<SubmitTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/submit`,
      payload.task,
    );

    yield all([
      put(actionCreator.SubmitAbsenceTaskSuccess()),
      put(
        actionCreator.GetOutstandingTasks({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        push(
          route("REPORTING_TOOLS_TASKS_PATH", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("THE_TASK_WAS_SUBMITTED_SUCCESSFULLY")),
    ]);
  } catch (e) {
    yield put(actionCreator.SubmitAbsenceTaskError(e.response));
  }
}

export function* onGetRtwInterviewTask({ payload }: Action<TaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data } = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.taskId}`,
    );

    const rtwInterviewTaskHistory = {
      completedBy: data.completedBy,
      completionDate: data.completionDate,
      questionAnswers: data.questionAnswers,
      type: data.type,
      id: data.id,
      dueDate: data.dueDate,
      rtwAt: data.rtwDate,
    };

    yield all([
      put(destroy("rtw-interview")),
      put(actionCreator.GetRtwInterviewTaskSuccess(data)),
      put(actionCreator.SetRtwInterviewTaskHistory(rtwInterviewTaskHistory)),
    ]);

    if (payload.redirectToTaskPage) {
      yield put(
        push(
          route("REPORTING_TASKS_FULL_PATH", {
            clientId: payload.clientId,
            taskId: payload.taskId,
            taskType: "interview",
          }),
        ),
      );
    }
  } catch (e) {
    yield put(actionCreator.GetRtwInterviewTaskError(e.response));
  }
}

export function* onSaveDraftRTWInterviewTask({
  payload,
}: Action<SubmitTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.taskId}`,
      payload.task,
    );

    yield put(startSubmit("rtw-interview"));

    yield all([
      put(actionCreator.SaveDraftRTWInterviewTaskSuccess()),
      put(actionCreator.GetRtwInterviewTaskSuccess(response.data)),
      put(stopSubmit("rtw-interview")),
      put(sendSuccessMessage("RTW_INTERVIEW_SAVED")),
    ]);
  } catch (e) {
    yield all(
      [put(actionCreator.SaveDraftRTWInterviewTaskError(e.response))],
      put(stopSubmit("rtw-interview")),
    );
  }
}

export function* onSubmitRTWInterviewTask({
  payload,
}: Action<SubmitTaskRequest>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.taskId}/submit`,
      payload.task,
    );

    yield all([
      put(actionCreator.GetOutstandingTasks({ clientId: payload.clientId })),
      put(
        actionCreator.GetOutstandingTasks({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(actionCreator.SubmitRTWInterviewTaskSuccess()),
      put(actionCreator.ToggleLateSubmissionModal(false)),
      put(sendSuccessMessage("RTW_INTERVIEW_SUBMITTED")),
    ]);

    if (!payload.isReports) {
      yield all([
        put(
          sharedActionCreator.GetEmployeeAbsence({
            clientId: payload.clientId,
            employeeId: payload.task.employee.id,
            absenceId: payload.task.absenceId,
            isReportingRequest: true,
          }),
        ),
        put(sharedActionCreator.SetVisibleAbsenceRateTrendRTWInterviewModal(false)),
        put(
          push(
            route("REPORTING_TOOLS_TASKS_PATH", {
              clientId: payload.clientId,
            }),
          ),
        ),
      ]);
    } else {
      const report = yield select(getCurrentReport);
      yield all([
        put(
          actionCreator.ToggleRtwInterviewModal({
            visible: false,
            rtwTask: null,
          }),
        ),
        put(
          actionCreator.GetReportingRTWDetailsReport({
            report: {
              filters: report.filters,
            },
            clientId: payload.clientId,
          }),
        ),
      ]);
    }
  } catch (e) {
    yield put(actionCreator.SubmitRTWInterviewTaskError(e.response));
  }
}

type CancelTaskPayload = $Exact<{
  clientId: number,
  taskId: number,
}>;

export function* onSubmitCancelTask({
  payload,
}: Action<CancelTaskPayload>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { clientId, taskId } = payload;
    yield call(http.post, `api/v1/tools/client/${clientId}/tools/task/${taskId}/cancel`);
    yield all([
      put(
        actionCreator.GetOutstandingTasks({
          clientId,
          taskId: null,
          search: {
            employee: "",
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreator.SubmitCancelTaskError(e.response));
  }
}

export function* TasksSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetOutstandingTasks, onGetOutstandingTasks),
    takeEvery(actions.GetAbsenceTaskSchema, onGetAbsenceTaskSchema),
    takeEvery(actions.GetAbsenceTask, onGetAbsenceTask),
    takeEvery(actions.GetManagerLedReferralTask, onGetManagerLedReferralTask),
    takeEvery(actions.UpdateManagerLedReferralDraft, onUpdateManagerLedReferralDraft),
    takeEvery(actions.SubmitManagerLedReferralDraft, onSubmitManagerLedReferralDraft),
    takeEvery(actions.SubmitAbsenceTask, onSubmitAbsenceTask),
    takeEvery(actions.GetRtwInterviewTask, onGetRtwInterviewTask),
    takeEvery(actions.SaveDraftRTWInterviewTask, onSaveDraftRTWInterviewTask),
    takeEvery(actions.SubmitRTWInterviewTask, onSubmitRTWInterviewTask),
    takeEvery(actions.SubmitCancelTask, onSubmitCancelTask),
  ]);
}

export default TasksSaga;
